import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSocial } from "@/lib/social";
import { MessageSquare, UserPlus, Check, X, Send, Ban, VolumeX, Radio, Search, Volume2 } from "lucide-react";
import EmotePicker, { EmoteButton } from "@/components/EmotePicker";
import ChatMessageText from "@/components/ChatMessageText";
import BlingUsername from "@/components/BlingUsername";

export default function Messenger() {
  const { user } = useAuth();
  const social = useSocial();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [active, setActive] = useState(params.get("to") || null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [addUsername, setAddUsername] = useState("");
  const [addMsg, setAddMsg] = useState("");
  const [tab, setTab] = useState("friends"); // friends | requests | blocks
  const [pickerOpen, setPickerOpen] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const insertAtCaret = (fragment) => {
    const el = inputRef.current;
    if (!el) { setText(t => t + fragment); return; }
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? text.length;
    const next = text.slice(0, start) + fragment + text.slice(end);
    setText(next);
    requestAnimationFrame(() => {
      try {
        el.focus();
        const pos = start + fragment.length;
        el.setSelectionRange(pos, pos);
      } catch { /* noop */ }
    });
  };

  useEffect(() => { if (!user) nav("/login"); }, [user, nav]);

  useEffect(() => {
    if (!active) return;
    api.get(`/dm/${active}`).then(r => setMessages(r.data.messages)).catch(() => setMessages([]));
    setParams({ to: active }, { replace: true });
    social.refreshFriends();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => {
    if (!social.dmEvent) return;
    if (active && (social.dmEvent.from_username === active || social.dmEvent.to_username === active)) {
      setMessages(m => [...m, social.dmEvent]);
    }
  }, [social.dmEvent, active]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  if (!user) return null;

  const send = async () => {
    const t = text.trim();
    if (!t || !active) return;
    setText("");
    try { await api.post(`/dm/${active}`, { text: t }); }
    catch (e) { setText(t); alert(e?.response?.data?.detail || "Send failed"); }
  };

  const doAdd = async (e) => {
    e.preventDefault();
    setAddMsg("");
    try {
      const r = await social.addFriend(addUsername.trim());
      setAddMsg(r?.status === "accepted" ? "Friend added!" : "Friend request sent");
      setAddUsername("");
    } catch (err) {
      setAddMsg(err?.response?.data?.detail || "Failed");
    }
  };

  const threadMap = Object.fromEntries(social.threads.map(t => [t.username, t]));

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-8 py-6 grid grid-cols-12 gap-4 h-[calc(100vh-4rem)]">
      {/* LEFT: Friends / Threads / Requests */}
      <aside className="col-span-12 lg:col-span-4 flex flex-col bg-[#18181C] border border-white/10 rounded-md overflow-hidden">
        <div className="p-4 border-b border-white/10">
          <div className="sc-label mb-3" style={{ color: "var(--sc-cyan)" }}>Messenger</div>
          <div className="flex gap-1 text-[10px] tracking-widest uppercase font-bold mb-3">
            {[
              { id: "friends", label: `Friends (${social.friends.length})` },
              { id: "requests", label: `Requests${social.requests.incoming.length ? ` · ${social.requests.incoming.length}` : ""}` },
              { id: "blocks", label: `Blocked (${social.blocks.length})` },
            ].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 px-2 py-1.5 rounded-sm border ${tab === t.id ? "border-[color:var(--sc-pink)] text-[color:var(--sc-pink)] bg-[color:var(--sc-pink)]/10" : "border-white/10 text-zinc-400 hover:border-white/20"}`}>
                {t.label}
              </button>
            ))}
          </div>
          <form onSubmit={doAdd} className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
              <input data-testid="add-friend-input" value={addUsername} onChange={e => setAddUsername(e.target.value)} placeholder="Add friend by username" className="sc-input !pl-8 text-sm" />
            </div>
            <button data-testid="add-friend-submit" type="submit" className="sc-btn text-xs flex items-center gap-1"><UserPlus className="w-3.5 h-3.5" /> Add</button>
          </form>
          {addMsg && <div className="text-[11px] text-zinc-400 mt-2">{addMsg}</div>}
        </div>

        <div className="flex-1 overflow-y-auto">
          {tab === "friends" && (
            <>
              {social.friends.length === 0 && (
                <div className="text-center text-xs text-zinc-500 py-10 px-6">
                  No friends yet. Add someone by username or right-click a chat message.
                </div>
              )}
              {social.friends.map(f => {
                const th = threadMap[f.username];
                return (
                  <button
                    key={f.id}
                    onClick={() => setActive(f.username)}
                    data-testid={`friend-row-${f.username}`}
                    className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/[0.03] transition flex items-center gap-3 ${active === f.username ? "bg-white/[0.04]" : ""}`}
                  >
                    <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold sc-heading text-sm relative"
                      style={{ background: "linear-gradient(135deg, #FF1053, #FF5900)" }}>
                      {f.username[0].toUpperCase()}
                      {f.is_live && <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[#18181C] bg-[color:var(--sc-volt)]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold flex items-center gap-1.5">{f.username}{f.is_live && <Radio className="w-3 h-3 text-[color:var(--sc-volt)]" />}</div>
                      {th ? (
                        <div className="text-[11px] text-zinc-500 truncate">{th.last_text}</div>
                      ) : (
                        <div className="text-[11px] text-zinc-600">Say hi</div>
                      )}
                    </div>
                    {th?.unread > 0 && (
                      <div className="min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold flex items-center justify-center bg-[color:var(--sc-pink)] text-white sc-mono">{th.unread}</div>
                    )}
                  </button>
                );
              })}
            </>
          )}

          {tab === "requests" && (
            <div className="p-3 space-y-3">
              <div>
                <div className="sc-label mb-2">Incoming</div>
                {social.requests.incoming.length === 0 && <div className="text-xs text-zinc-500 py-2">No incoming.</div>}
                {social.requests.incoming.map(r => (
                  <div key={r.id} className="flex items-center justify-between p-2 border border-white/10 rounded-sm mb-2">
                    <div className="text-sm">{r.from_username}</div>
                    <div className="flex gap-1">
                      <button data-testid={`accept-${r.from_username}`} onClick={() => social.acceptRequest(r.id)} className="p-1.5 rounded-sm bg-[color:var(--sc-volt)] text-black" title="Accept"><Check className="w-3.5 h-3.5" /></button>
                      <button data-testid={`decline-${r.from_username}`} onClick={() => social.declineRequest(r.id)} className="p-1.5 rounded-sm border border-white/10 text-zinc-400" title="Decline"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
              <div>
                <div className="sc-label mb-2">Outgoing</div>
                {social.requests.outgoing.length === 0 && <div className="text-xs text-zinc-500 py-2">No pending outgoing.</div>}
                {social.requests.outgoing.map(r => (
                  <div key={r.id} className="text-xs text-zinc-400 p-2 border border-white/5 rounded-sm mb-1">Waiting for {r.to_username}</div>
                ))}
              </div>
            </div>
          )}

          {tab === "blocks" && (
            <div className="p-3">
              {social.blocks.length === 0 && <div className="text-xs text-zinc-500 py-4 text-center">No blocked users.</div>}
              {social.blocks.map(b => (
                <div key={b.blocked_user_id} className="flex items-center justify-between p-2 border border-white/10 rounded-sm mb-2">
                  <div className="text-sm text-zinc-300 flex items-center gap-2"><Ban className="w-3.5 h-3.5 text-[color:var(--sc-pink)]" /> {b.blocked_username}</div>
                  <button data-testid={`unblock-${b.blocked_username}`} onClick={() => social.unblockUser(b.blocked_username)} className="sc-btn-ghost text-xs">Unblock</button>
                </div>
              ))}
              <div className="mt-4 sc-label" style={{ color: "var(--sc-muted)" }}>Muted</div>
              {social.mutes.length === 0 && <div className="text-xs text-zinc-500 py-2 text-center">No muted users.</div>}
              {social.mutes.map(m => (
                <div key={m.muted_user_id} className="flex items-center justify-between p-2 border border-white/10 rounded-sm mb-2">
                  <div className="text-sm text-zinc-300 flex items-center gap-2"><VolumeX className="w-3.5 h-3.5 text-[color:var(--sc-orange)]" /> {m.muted_username}</div>
                  <button data-testid={`unmute-${m.muted_username}`} onClick={() => social.unmuteUser(m.muted_username)} className="sc-btn-ghost text-xs flex items-center gap-1"><Volume2 className="w-3 h-3" /> Unmute</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </aside>

      {/* RIGHT: Chat pane */}
      <section className="col-span-12 lg:col-span-8 flex flex-col bg-[#18181C] border border-white/10 rounded-md overflow-hidden">
        {!active ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
            <MessageSquare className="w-12 h-12 text-zinc-600 mb-4" />
            <div className="sc-heading text-2xl font-bold">Select a friend</div>
            <div className="text-sm text-zinc-500 mt-2 max-w-md">Choose a conversation from the list. Add friends by username, or right-click any chat message in a channel to send a friend request.</div>
          </div>
        ) : (
          <>
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <div>
                <div className="sc-heading text-lg font-bold">
                  <BlingUsername username={active} defaultColor="#FFFFFF" suffix="" testid={`dm-header-${active}`} />
                </div>
                <div className="text-[11px] sc-mono text-zinc-500">Direct message</div>
              </div>
              <div className="flex gap-2">
                <button data-testid={`msg-mute-${active}`} onClick={() => social.isMuted(active) ? social.unmuteUser(active) : social.muteUser(active)} className="sc-btn-ghost text-xs flex items-center gap-1">
                  {social.isMuted(active) ? <><Volume2 className="w-3.5 h-3.5" /> Unmute</> : <><VolumeX className="w-3.5 h-3.5" /> Mute</>}
                </button>
                <button data-testid={`msg-block-${active}`} onClick={() => social.isBlocked(active) ? social.unblockUser(active) : social.blockUser(active)} className="sc-btn-ghost text-xs flex items-center gap-1">
                  <Ban className="w-3.5 h-3.5" /> {social.isBlocked(active) ? "Unblock" : "Block"}
                </button>
              </div>
            </div>
            <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-2">
              {messages.length === 0 && <div className="text-center text-xs text-zinc-500 py-10">No messages yet — say hi.</div>}
              {messages.map(m => {
                const mine = m.from_user_id === user.id;
                const lfg = m.lfg_ref;
                const lfgAccent = lfg
                  ? (lfg.kind === "response"
                      ? (lfg.status === "accepted" ? "var(--sc-volt)" : "var(--sc-pink)")
                      : "var(--sc-cyan)")
                  : null;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[75%] px-3 py-2 rounded-md ${mine ? "bg-[color:var(--sc-pink)] text-white" : "bg-[#0c0c0e] border border-white/10 text-zinc-100"} ${lfg ? "ring-1" : ""}`}
                      style={lfg ? { boxShadow: `0 0 0 1px ${lfgAccent}66 inset` } : undefined}
                      data-testid={lfg ? `dm-lfg-${lfg.kind}-${m.id}` : undefined}
                    >
                      {lfg && (
                        <div className="flex items-center gap-1.5 mb-1.5 text-[9px] sc-mono uppercase tracking-widest" style={{ color: lfgAccent }}>
                          <span>🎮</span>
                          <span>{lfg.kind === "response" ? `LFG Response — ${lfg.status || "update"}` : "LFG Application"}</span>
                        </div>
                      )}
                      <div className="text-sm whitespace-pre-wrap break-words">
                        <ChatMessageText text={m.text} />
                      </div>
                      <div className={`text-[10px] mt-1 opacity-70 sc-mono ${mine ? "text-white/70" : "text-zinc-500"} flex items-center gap-2`}>
                        <span>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                        {lfg && (
                          <button
                            data-testid={`dm-lfg-view-${m.id}`}
                            onClick={() => nav(`/lfg?post=${encodeURIComponent(lfg.post_id)}`)}
                            className={`underline hover:opacity-100 opacity-90 ${mine ? "text-white" : "text-[color:var(--sc-cyan)]"}`}
                          >
                            View post
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="p-3 border-t border-white/10 flex gap-2 items-end relative">
              <input ref={inputRef} data-testid="dm-input" value={text} onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); send(); setPickerOpen(false); } }} placeholder={social.isBlocked(active) ? "Unblock to send messages" : "Message..."} disabled={social.isBlocked(active)} className="sc-input flex-1 min-w-0" maxLength={2000} />
              {!social.isBlocked(active) && (
                <EmoteButton onClick={() => setPickerOpen(v => !v)} active={pickerOpen} />
              )}
              <button data-testid="dm-send" onClick={() => { send(); setPickerOpen(false); }} disabled={social.isBlocked(active)} className="sc-btn flex items-center gap-1.5"><Send className="w-3.5 h-3.5" /></button>
              {pickerOpen && (
                <EmotePicker open={pickerOpen} onClose={() => setPickerOpen(false)} onInsert={insertAtCaret} />
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
